import React, { useState } from "react";

const S = {
  page: (dark) => ({
    minHeight: "100vh",
    padding: "3rem",
    background: dark ? "#0f172a" : "#f8fafc",
    color: dark ? "#f1f5f9" : "#0f172a",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    transition: "background 0.3s, color 0.3s",
  }),
  inner: {
    maxWidth: 900,
    margin: "0 auto",
  },
  controls: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "0.75rem",
    marginBottom: "2.5rem",
  },
  btn: (dark) => ({
    padding: "0.4rem 1rem",
    borderRadius: "1rem",
    border: `1px solid ${dark ? "#475569" : "#cbd5e1"}`,
    background: "transparent",
    color: dark ? "#f1f5f9" : "#0f172a",
    cursor: "pointer",
    fontSize: "0.875rem",
    fontWeight: 500,
  }),
  card: (dark) => ({
    background: dark ? "#1e293b" : "#ffffff",
    border: `1px solid ${dark ? "#334155" : "#e2e8f0"}`,
    borderRadius: "1rem",
    padding: "1.5rem",
    marginBottom: "1.5rem",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  }),
  grid3: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "1.25rem",
    marginBottom: "1.5rem",
  },
  h1: {
    fontSize: "2rem",
    fontWeight: 700,
    margin: "0 0 0.4rem",
    letterSpacing: "-0.02em",
  },
  h2: {
    fontSize: "1.125rem",
    fontWeight: 600,
    margin: "0 0 0.75rem",
  },
  h3: {
    fontSize: "0.975rem",
    fontWeight: 600,
    margin: "0 0 0.4rem",
  },
  subtitle: {
    fontSize: "1.1rem",
    opacity: 0.75,
    margin: "0 0 1rem",
  },
  meta: {
    display: "flex",
    flexWrap: "wrap",
    gap: "1rem",
    fontSize: "0.875rem",
    opacity: 0.65,
    marginBottom: "2.5rem",
  },
  metaItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.35rem",
  },
  badge: (dark) => ({
    display: "inline-block",
    padding: "0.2rem 0.65rem",
    borderRadius: "0.4rem",
    fontSize: "0.8rem",
    fontWeight: 500,
    background: dark ? "#334155" : "#f1f5f9",
    color: dark ? "#94a3b8" : "#475569",
    border: `1px solid ${dark ? "#475569" : "#e2e8f0"}`,
    marginRight: "0.35rem",
    marginBottom: "0.35rem",
  }),
  badges: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: "0.5rem",
  },
  ul: {
    paddingLeft: "1.25rem",
    margin: "0.4rem 0 0",
    opacity: 0.8,
    lineHeight: 1.7,
  },
  expBlock: {
    marginBottom: "1.5rem",
  },
};

export default function CvAntoineCailliez2026() {
  const [dark, setDark] = useState(false);
  const [lang, setLang] = useState("FR");

  const t = {
    EN: {
      title: "Full-Stack Developer • AI & Automation Builder",
      profileTitle: "Profile",
      profile:
        "Builder-minded full-stack developer with 15+ years in web platforms. Specialized in Python APIs, automation workflows, and generative AI integration. Comfortable shipping fast in early-stage environments.",
      experience: "Experience",
      education: "Education",
      backend: "Backend",
      frontend: "Frontend",
      ai: "AI & Automation",
      eduText: "Engineering Degree (Computer Science) — HEI",
      english: "English: C1",
    },
    FR: {
      title: "Développeur Full-Stack • IA & Automatisation",
      profileTitle: "Profil",
      profile:
        "Développeur full-stack orienté produit avec 15+ ans d'expérience. Spécialisé en API Python, automatisation de workflows et intégration d'IA générative. À l'aise dans les environnements early-stage.",
      experience: "Expérience",
      education: "Formation",
      backend: "Backend",
      frontend: "Frontend",
      ai: "IA & Automatisation",
      eduText: "Diplôme d'Ingénieur (Informatique) — HEI",
      english: "Anglais : C1",
    },
  };

  const tx = t[lang];

  return (
    <div style={S.page(dark)}>
      <div style={S.inner}>
        {/* Controls */}
        <div style={S.controls}>
          <button style={S.btn(dark)} onClick={() => setLang(lang === "EN" ? "FR" : "EN")}>
            {lang === "EN" ? "FR" : "EN"}
          </button>
          <button style={S.btn(dark)} onClick={() => setDark(!dark)}>
            {dark ? "☀️" : "🌙"}
          </button>
        </div>

        {/* Header */}
        <h1 style={S.h1}>Antoine Cailliez</h1>
        <p style={S.subtitle}>{tx.title}</p>
        <div style={S.meta}>
          <span style={S.metaItem}>📍 Remote / France</span>
          <span style={S.metaItem}>✉️ acailliez@gmail.com</span>
          <span style={S.metaItem}>📞 06 12 70 52 35</span>
        </div>

        {/* Profile */}
        <div style={S.card(dark)}>
          <h2 style={S.h2}>{tx.profileTitle}</h2>
          <p style={{ margin: 0, opacity: 0.8, lineHeight: 1.7 }}>{tx.profile}</p>
        </div>

        {/* Skills */}
        <div style={S.grid3}>
          <div style={S.card(dark)}>
            <h2 style={S.h2}>{tx.backend}</h2>
            <div style={S.badges}>
              {["Python", "FastAPI", "PostgreSQL", "Java / Spring", "REST APIs"].map(b => (
                <span key={b} style={S.badge(dark)}>{b}</span>
              ))}
            </div>
          </div>
          <div style={S.card(dark)}>
            <h2 style={S.h2}>{tx.frontend}</h2>
            <div style={S.badges}>
              {["React", "Angular", "HTML / CSS"].map(b => (
                <span key={b} style={S.badge(dark)}>{b}</span>
              ))}
            </div>
          </div>
          <div style={S.card(dark)}>
            <h2 style={S.h2}>{tx.ai}</h2>
            <div style={S.badges}>
              {["Generative AI", "n8n", "Workflow Automation", "Prompt Engineering", "Docker"].map(b => (
                <span key={b} style={S.badge(dark)}>{b}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Experience */}
        <div style={S.card(dark)}>
          <h2 style={S.h2}>{tx.experience}</h2>
          <div style={S.expBlock}>
            <h3 style={S.h3}>AI &amp; Automation Developer — 2024–Present</h3>
            <ul style={S.ul}>
              <li>Built FastAPI backends</li>
              <li>Implemented n8n workflows</li>
              <li>Integrated generative AI assistants</li>
            </ul>
          </div>
          <div style={{ ...S.expBlock, marginBottom: 0 }}>
            <h3 style={S.h3}>Full-Stack Developer — 2007–Present</h3>
            <ul style={S.ul}>
              <li>Developed large-scale web platforms</li>
              <li>Optimized application performance</li>
              <li>Participated in architectural decisions</li>
            </ul>
          </div>
        </div>

        {/* Education */}
        <div style={S.card(dark)}>
          <h2 style={S.h2}>{tx.education}</h2>
          <p style={{ margin: "0 0 0.25rem", opacity: 0.8 }}>{tx.eduText}</p>
          <p style={{ margin: 0, opacity: 0.6, fontSize: "0.875rem" }}>{tx.english}</p>
        </div>
      </div>
    </div>
  );
}
